import { ExternalLink, Search, Clock, Calendar, Plane, FileText, Timer } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LocalizedLink } from "@/components/LocalizedLink";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";

const Tools = () => {
  const tools = [
    {
      title: "USCIS Case Status",
      description: "Check the status of your USCIS case using your receipt number",
      url: "https://egov.uscis.gov/casestatus/landing.do",
      icon: Search,
    },
    {
      title: "USCIS Processing Times",
      description: "View current processing times for all USCIS forms and service centers",
      url: "https://egov.uscis.gov/processing-times/",
      icon: Clock,
    },
    {
      title: "Visa Bulletin",
      description: "Check visa availability and priority dates for family and employment-based immigration",
      url: "https://travel.state.gov/content/travel/en/legal/visa-law0/visa-bulletin.html",
      icon: Calendar,
    },
    {
      title: "I-94 (Arrival/Departure Record)",
      description: "Retrieve your I-94 admission record and travel history",
      url: "https://i94.cbp.dhs.gov/",
      icon: Plane,
    },
    {
      title: "Visa Appointment Wait Times",
      description: "Check visa interview appointment wait times at U.S. embassies and consulates worldwide",
      url: "https://travel.state.gov/content/travel/en/us-visas/visa-information-resources/wait-times.html",
      icon: Timer,
    },
    {
      title: "Immigration Forms (USCIS)",
      description: "Download official USCIS immigration forms and instructions",
      url: "https://www.uscis.gov/forms",
      icon: FileText,
    },
  ];

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-hero text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Immigration Tools & Resources</h1>
            <p className="text-xl opacity-95">
              Quick access to important immigration tools and government resources
            </p>
          </div>
        </div>
      </section>

      {/* Tools Grid */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map((tool, index) => (
              <Card key={index} className="hover:shadow-xl transition-all hover:-translate-y-1 border-t-4 border-t-accent">
                <CardHeader>
                  <div className="h-14 w-14 rounded-xl bg-gradient-gold-shine flex items-center justify-center mb-4 shadow-gold">
                    <tool.icon className="h-7 w-7 text-primary" />
                  </div>
                  <CardTitle className="text-xl">{tool.title}</CardTitle>
                  <CardDescription>{tool.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild variant="default" className="w-full">
                    <a href={tool.url} target="_blank" rel="noopener noreferrer">
                      Visit Site <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 max-w-4xl mx-auto bg-card p-8 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-4">Need Help Navigating These Tools?</h2>
            <p className="text-muted-foreground mb-6">
              These government websites can be confusing and difficult to navigate. If you need assistance understanding your case status, processing times, or any immigration matter, our experienced attorneys are here to help.
            </p>
            <Button asChild variant="cta" size="lg">
              <LocalizedLink to="/appointment">Schedule Consultation</LocalizedLink>
            </Button>
          </div>
        </div>
      </section>

      <CTASection
        title="Let Us Handle the Paperwork"
        description="Immigration forms and procedures can be overwhelming. Our experienced team can guide you through every step of your immigration journey."
      />

      <Footer />
    </div>
  );
};

export default Tools;
